import {
  getSkipCleanupFlag,
  getTelemetryEventQueueSnapshot,
  removeTelemetryEventsAtIndexes,
} from '@ms/yammer-telemetry-store';

import type { LogEvent, LogEventInput } from '../events';

import {
  KeepaliveRequestBodyLimitInBytes,
  getKeepaliveRequestBodySize,
  getLogEventsWithinKeepaliveRequestBodyLimit,
} from './getLogEventsWithinKeepaliveRequestBodyLimit';
import { getLogEventInputWithContext } from './getReportLogEventsInput';
import type { PageHideDeliveryReason, PageHideDeliveryTreatment } from './reportDeliveryLifecycleInfo';
import { createDeliveryLifecycleInfoEvent, isProductLogEvent } from './reportDeliveryLifecycleInfo';
import { reportLogEventsWithKeepalive } from './reportTelemetryEventsToServer';

const pageHideQueueEventTypes = ['Error', 'Info', 'Performance'] as const;

export interface PageHideMeasurementContext {
  readonly attempts: number;
  readonly persisted: boolean;
}

interface QueuedProductLogEvent {
  readonly event: LogEvent;
  readonly eventType: LogEvent['type'];
  readonly queueIndex: number;
}

const getQueuedProductLogEvents = (): QueuedProductLogEvent[] =>
  pageHideQueueEventTypes.flatMap((eventType) =>
    getTelemetryEventQueueSnapshot<LogEvent>(eventType)
      .map((event, queueIndex) => ({ event, eventType, queueIndex }))
      .filter(({ event }) => isProductLogEvent(event))
  );

interface QueuedProductLogEventInput extends QueuedProductLogEvent {
  readonly input: LogEventInput;
}

const createPageHideReceiptEvent = (
  measurementContext: PageHideMeasurementContext,
  treatment: PageHideDeliveryTreatment,
  resultCount: number,
  apiCount: number,
  encodedBodySize: number,
  reason?: PageHideDeliveryReason
) =>
  getLogEventInputWithContext(
    createDeliveryLifecycleInfoEvent({
      apiCount,
      attempts: measurementContext.attempts,
      encodedBodySize,
      eventInputType: 'Log',
      outcome: 'pagehide_queue_observed',
      persisted: measurementContext.persisted,
      reason,
      resultCount,
      skipAuthentication: true,
      treatment,
    })
  ).event;

const createPayloadWithExactSize = (
  measurementContext: PageHideMeasurementContext,
  treatment: PageHideDeliveryTreatment,
  resultCount: number,
  selectedEvents: LogEventInput[],
  reason?: PageHideDeliveryReason
) => {
  const apiCount = selectedEvents.length;
  let encodedBodySize = 0;
  let events = [
    createPageHideReceiptEvent(measurementContext, treatment, resultCount, apiCount, encodedBodySize, reason),
    ...selectedEvents,
  ];
  let nextEncodedBodySize = getKeepaliveRequestBodySize(events);

  while (nextEncodedBodySize !== encodedBodySize) {
    encodedBodySize = nextEncodedBodySize;
    events = [
      createPageHideReceiptEvent(measurementContext, treatment, resultCount, apiCount, encodedBodySize, reason),
      ...selectedEvents,
    ];
    nextEncodedBodySize = getKeepaliveRequestBodySize(events);
  }

  return events;
};

const removeSelectedEventsFromQueues = (
  queuedProductLogEventInputs: QueuedProductLogEventInput[],
  selectedEvents: LogEventInput[]
) => {
  const selectedInputs = new Set(selectedEvents);
  const queueIndexesByEventType = new Map<LogEvent['type'], number[]>();

  queuedProductLogEventInputs
    .filter(({ input }) => selectedInputs.has(input))
    .forEach(({ eventType, queueIndex }) => {
      const queueIndexes = queueIndexesByEventType.get(eventType) ?? [];
      queueIndexes.push(queueIndex);
      queueIndexesByEventType.set(eventType, queueIndexes);
    });

  queueIndexesByEventType.forEach((queueIndexes, eventType) => {
    removeTelemetryEventsAtIndexes(eventType, queueIndexes);
  });
};

type ReportLogQueueForPageHide = (
  measurementContext: PageHideMeasurementContext,
  shouldDeliverQueuedLogEvents?: boolean
) => void;
export const reportLogQueueForPageHide: ReportLogQueueForPageHide = (
  measurementContext,
  shouldDeliverQueuedLogEvents = false
) => {
  const shouldSkipCleanup = getSkipCleanupFlag() === true;
  const queuedProductLogEvents = getQueuedProductLogEvents();
  const resultCount = queuedProductLogEvents.length;
  if (resultCount === 0) {
    return;
  }

  const treatment = shouldDeliverQueuedLogEvents ? 'treatment' : 'control';
  if (shouldSkipCleanup) {
    reportLogEventsWithKeepalive(
      createPayloadWithExactSize(measurementContext, treatment, resultCount, [], 'skip_next_cleanup')
    );

    return;
  }

  if (!shouldDeliverQueuedLogEvents) {
    reportLogEventsWithKeepalive(createPayloadWithExactSize(measurementContext, treatment, resultCount, []));

    return;
  }

  const queuedProductLogEventInputs = queuedProductLogEvents.map((queuedProductLogEvent) => ({
    ...queuedProductLogEvent,
    input: getLogEventInputWithContext(queuedProductLogEvent.event).event,
  }));
  const conservativeReceiptEvent = createPageHideReceiptEvent(
    measurementContext,
    treatment,
    resultCount,
    resultCount,
    KeepaliveRequestBodyLimitInBytes
  );
  const selectedEvents = getLogEventsWithinKeepaliveRequestBodyLimit(
    conservativeReceiptEvent,
    queuedProductLogEventInputs.map(({ input }) => input)
  ).slice(1);
  const didStartRequest = reportLogEventsWithKeepalive(
    createPayloadWithExactSize(measurementContext, treatment, resultCount, selectedEvents)
  );

  if (didStartRequest) {
    removeSelectedEventsFromQueues(queuedProductLogEventInputs, selectedEvents);
  }
};
